import { tasksService } from './api';
import { apiCache } from '../utils/apiCache';

// Cached wrapper for tasks service
export const cachedTasksService = {
  // Get dashboard data with caching
  getDashboardData: async (params = {}, maxAge = 2 * 60 * 1000) => { // 2 minutes cache
    const cacheKey = '/tasks/api/tasks/dashboard_data/';

    console.log('🔍 CachedService: getDashboardData called with params:', params);

    // Check cache first
    const cached = apiCache.get(cacheKey, params, maxAge);
    if (cached) {
      console.log('📦 CachedService: Returning cached data:', cached);
      return { data: cached };
    }

    console.log('🌐 CachedService: Cache miss, making API call...');

    // Check for pending request
    const pending = apiCache.getPendingRequest(cacheKey, params);
    if (pending) {
      console.log('⏳ CachedService: Returning pending request');
      return pending;
    }

    // Make new request
    const promise = tasksService.getDashboardData(params);
    apiCache.setPendingRequest(cacheKey, params, promise);

    try {
      const response = await promise;
      console.log('✅ CachedService: API call successful, response data:', response.data);
      // Cache the response data
      apiCache.set(cacheKey, params, response.data);
      return response;
    } catch (error) {
      console.error('❌ CachedService: API call failed:', error);
      // Don't cache errors
      throw error;
    }
  },

  // Get tasks with caching and deduplication
  getTasks: async (params = {}, maxAge = 30 * 1000) => { // 30 seconds cache for tasks list
    const cacheKey = '/tasks/api/tasks/';

    // Check cache first
    const cached = apiCache.get(cacheKey, params, maxAge);
    if (cached) {
      return { data: cached };
    }

    // Check for pending request
    const pending = apiCache.getPendingRequest(cacheKey, params);
    if (pending) {
      return pending;
    }

    // Make new request
    const promise = tasksService.getTasks(params);
    apiCache.setPendingRequest(cacheKey, params, promise);

    try {
      const response = await promise;
      // Cache the response data
      apiCache.set(cacheKey, params, response.data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Get single task with caching
  getTask: async (id, maxAge = 1 * 60 * 1000) => { // 1 minute cache
    const cacheKey = `/tasks/api/tasks/${id}/`;
    const params = {};

    const cached = apiCache.get(cacheKey, params, maxAge);
    if (cached) {
      return { data: cached };
    }

    const pending = apiCache.getPendingRequest(cacheKey, params);
    if (pending) {
      return pending;
    }

    const promise = tasksService.getTask(id);
    apiCache.setPendingRequest(cacheKey, params, promise);

    try {
      const response = await promise;
      apiCache.set(cacheKey, params, response.data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Invalidate cache methods
  invalidateDashboardCache: () => {
    apiCache.delete('/tasks/api/tasks/dashboard_data/', {});
  },

  invalidateTasksCache: () => {
    // Clear all tasks cache entries
    for (const key of apiCache.cache.keys()) {
      if (key.includes('/tasks/api/tasks/')) {
        apiCache.cache.delete(key);
      }
    }
  },

  invalidateTaskCache: (id) => {
    apiCache.delete(`/tasks/api/tasks/${id}/`, {});
  },

  // Pass through methods that shouldn't be cached (mutations)
  createTask: (data) => {
    cachedTasksService.invalidateTasksCache();
    cachedTasksService.invalidateDashboardCache();
    return tasksService.createTask(data);
  },
  updateTask: (id, data) => {
    // Invalidate caches on update
    cachedTasksService.invalidateTaskCache(id);
    cachedTasksService.invalidateTasksCache();
    cachedTasksService.invalidateDashboardCache();
    return tasksService.updateTask(id, data);
  },
  deleteTask: (id) => {
    // Invalidate caches on delete
    cachedTasksService.invalidateTaskCache(id);
    cachedTasksService.invalidateTasksCache();
    cachedTasksService.invalidateDashboardCache();
    return tasksService.deleteTask(id);
  },
  assignTask: (data) => {
    // Invalidate caches on assignment
    cachedTasksService.invalidateTasksCache();
    cachedTasksService.invalidateDashboardCache();
    return tasksService.assignTask(data);
  },
  bulkUpdateTasks: (data) => {
    cachedTasksService.invalidateTasksCache();
    cachedTasksService.invalidateDashboardCache();
    return tasksService.bulkUpdateTasks(data);
  },
  bulkDeleteTasks: (taskIds) => {
    taskIds.forEach(id => cachedTasksService.invalidateTaskCache(id));
    cachedTasksService.invalidateTasksCache();
    cachedTasksService.invalidateDashboardCache();
    return tasksService.bulkDeleteTasks(taskIds);
  },

  // Other pass-through methods
  getTaskDependencies: tasksService.getTaskDependencies,
  addTaskDependency: tasksService.addTaskDependency,
  removeTaskDependency: tasksService.removeTaskDependency,
  getTaskComments: tasksService.getTaskComments,
  addTaskComment: tasksService.addTaskComment,
  updateTaskComment: tasksService.updateTaskComment,
  deleteTaskComment: tasksService.deleteTaskComment,
  getTaskTemplates: tasksService.getTaskTemplates,
  getTaskCategories: tasksService.getTaskCategories,
  getTaskPriorities: tasksService.getTaskPriorities,
  getTaskSources: tasksService.getTaskSources,
  getTaskAnalytics: tasksService.getTaskAnalytics,
  exportTasks: tasksService.exportTasks,
  exportProjects: tasksService.exportProjects,
  getKPIs: tasksService.getKPIs,
  getChartData: tasksService.getChartData,
};

export default cachedTasksService;